import { useEffect, useRef, useState } from "react";
import { DailyItemType, PlannerDatabaseType } from "../types/Planner";
import { GeneralObject } from "../types/General";
import { dayInMilliseconds, getDayIndex, ISOStringWithTimeZone } from "../helpers/DateAndTimeHelpers";

function useTestAndTimeSum(data: PlannerDatabaseType, startIndex = 0) {    
    const [timeSum, setTimeSum] = useState(0);
    const [testSum, setTestSum] = useState(0);
    const [dailyBasedStudentTimeSum, setDailyBasedStudentTimeSum] = useState<GeneralObject<number>>({});
    const [dailyBasedStudentTestSum, setDailyBasedStudentTestSum] = useState<GeneralObject<number>>({});
    const [dailyBasedTestSum, setDailyBasedTestSum] = useState<GeneralObject<number>>({});
    const [dailyBasedTimeSum, setDailyBasedTimeSum] = useState<GeneralObject<number>>({});
    const [lessonBasedTestSum, setLessonBasedTestSum] = useState<GeneralObject<number>>({});
    const [lessonBasedTimeSum, setLessonBasedTimeSum] = useState<GeneralObject<number>>({});

    function dailySum(dayData: (DailyItemType | null)[], lessonBasedTestSum: GeneralObject<number>, 
            lessonBasedStudySum: GeneralObject<number>, ignoreLessonBase: boolean) {

        let testSum = 0, timeSum = 0, studentTestSum = 0, studentTimeSum = 0;

        dayData.forEach((item) => {
            if (item && item.metadata) {
                if (item.role === 1) {
                    if (!ignoreLessonBase) {
                        const currentStudySum = (lessonBasedStudySum[item.metadata.lesson.id] || 0);
                        const currentTestSum = (lessonBasedTestSum[item.metadata.lesson.id] || 0);

                        lessonBasedStudySum[item.metadata.lesson.id] = currentStudySum + item.metadata.plannedTime;
                        lessonBasedTestSum[item.metadata.lesson.id] = currentTestSum + item.metadata.plannedTest;
                    }

                    timeSum += item.metadata.plannedTime;
                    testSum += item.metadata.plannedTest;
                }

                studentTimeSum += item.time || 0;

                if (item.type === 1 || item.type === 4)
                    studentTestSum += item.metadata.testCount || 0;
            }
        })

        return { testSum: testSum, timeSum: timeSum, studentTestSum: studentTestSum, studentTimeSum: studentTimeSum }
    }

    useEffect(() => {
        let testSum = 0, timeSum = 0;
        let dailyBasedTestSum = {} as GeneralObject<number>;
        let dailyBasedTimeSum = {} as GeneralObject<number>;
        let studentDailyBasedTestSum = {} as GeneralObject<number>;
        let studentDailyBasedTimeSum = {} as GeneralObject<number>;
        let lessonBasedTestSum = {} as GeneralObject<number>;
        let lessonBasedTimeSum = {} as GeneralObject<number>;

        const todayIndex = getDayIndex();
        const firstWeekOfDay = new Date(new Date().getTime() + ((startIndex - todayIndex) * dayInMilliseconds));
        const lastWeekOfDay = new Date(firstWeekOfDay.getTime() + ((startIndex === 0 ? 6 : 7) * dayInMilliseconds));

        const firstDayOfWeekISO = ISOStringWithTimeZone(firstWeekOfDay);
        const lastWeekOfDayISO = ISOStringWithTimeZone(lastWeekOfDay);

        Object.keys(data as PlannerDatabaseType).forEach((item) => {
            const sum = dailySum(data[item], lessonBasedTestSum, lessonBasedTimeSum, firstDayOfWeekISO > item || lastWeekOfDayISO < item);

            dailyBasedTestSum[item] = sum.testSum;
            dailyBasedTimeSum[item] = sum.timeSum;
            studentDailyBasedTestSum[item] = sum.studentTestSum;
            studentDailyBasedTimeSum[item] = sum.studentTimeSum;

            if (firstDayOfWeekISO <= item && lastWeekOfDayISO >= item) {
                testSum += sum.testSum;
                timeSum += sum.timeSum;
            }
        })

        setTestSum(testSum);
        setTimeSum(timeSum);
        setDailyBasedTestSum(dailyBasedTestSum);
        setDailyBasedTimeSum(dailyBasedTimeSum);
        setLessonBasedTestSum(lessonBasedTestSum);
        setLessonBasedTimeSum(lessonBasedTimeSum);
        setDailyBasedStudentTestSum(studentDailyBasedTestSum);
        setDailyBasedStudentTimeSum(studentDailyBasedTimeSum);
    }, [data, startIndex])

    return {
        testSum,
        timeSum,
        dailyBasedTestSum,
        dailyBasedTimeSum,
        lessonBasedTimeSum,
        lessonBasedTestSum,
        dailyBasedStudentTestSum,
        dailyBasedStudentTimeSum
    }
}

export default useTestAndTimeSum;